import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Loader2, Users, Search } from "lucide-react";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { useAccountCustomers } from "@/domains/customers/services/account-customers";
import { useAddCustomerToServiceLine } from "@/domains/service-lines/services/account-service-lines";
import { ServiceLine } from "@/shared/types/serviceLineTypes";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import { Customer } from "@/shared/types/customerTypes";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import EmptyView from "@/shared/components/EmptyView";
import SearchBar from "@/shared/components/SearchBar";

interface AddCustomerToServiceLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  serviceLine: ServiceLine;
}

const AddCustomerToServiceLineDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine
}: AddCustomerToServiceLineDialogProps) => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  //TODO: Implement infinite scroll for customers
  const { data: customers, isLoading: isLoadingCustomers } = useAccountCustomers(open);
  const addCustomerToServiceLine = useAddCustomerToServiceLine();

  // Filter out customers that are already assigned to this service line
  const availableCustomers = useMemo(() => {
    return customers?.filter(customer =>
      customer?.customerId !== serviceLine.customer?.customerId) || [];
  }, [customers, serviceLine]);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    return availableCustomers.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.customerId.toString().includes(searchTerm)
    ) || [];
  }, [availableCustomers, searchTerm]);

  useEffect(() => {
    if (!open) {
      setSelectedCustomer(null);
      setSearchTerm("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer) {
      showError(ERROR.SERVICE_LINE.SELECT_CUSTOMER);
      return;
    }

    try {
      await
        addCustomerToServiceLine.mutateAsync({
          customerId: selectedCustomer.customerId,
          serviceLineNumber: serviceLine.serviceLineNumber
        })
      showSuccess(SUCCESS.SERVICE_LINE.ADDED_CUSTOMER);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.ADD_CUSTOMER);
    }
  };

  const showSearch = availableCustomers.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="add-customer-to-service-line-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[600px] sm:max-h-[600px]">
        <DialogHeader>
          <DialogTitle>Add Customer to Service Line</DialogTitle>
          <DialogDescription>{serviceLine?.serviceLineName}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 flex flex-col h-full">
          <div className="grid grid-cols-1 gap-4 flex-1">
            <div className="space-y-2 flex flex-col h-full">
              {/* Search Input - only show when there are more than 2 customers */}
              {showSearch && (
                <div className="relative">
                  <SearchBar value={searchTerm} onChange={(value) => setSearchTerm(value)} placeholder="Search customers..." />
                </div>
              )}

              <p className="text-sm font-medium">Select Customer</p>
              <ScrollArea className="rounded-md border border p-4 flex-1 min-h-[300px]">
                {isLoadingCustomers ? (
                  <div className="flex justify-center items-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : filteredCustomers.length > 0 ? (
                  <RadioGroup
                    value={selectedCustomer?.customerId.toString()}
                    onValueChange={(value) => {
                      const customer = filteredCustomers.find(c => c.customerId === value);
                      setSelectedCustomer(customer || null);
                    }}
                    className="space-y-2"
                  >
                    {filteredCustomers.map((customer) => (
                      <div key={customer.customerId} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={customer.customerId.toString()}
                          id={`customer-${customer.customerId}`}
                        />
                        <Label
                          htmlFor={`customer-${customer.customerId}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          <div className="flex flex-col">
                            <span>{customer.name}</span>
                            <span className="text-xs">ID: {customer.customerId}</span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : searchTerm ? (
                  <EmptyView
                    icon={Search}
                    size="small"
                    title="No Customers Found"
                    description={`No customers match "${searchTerm}". Try adjusting your search.`}
                  />
                ) : (
                  <EmptyView
                    icon={Users}
                    size="small"
                    title="No Customers Available"
                    description="There are no customers available to add this service line to."
                  />
                )}
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={addCustomerToServiceLine.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={addCustomerToServiceLine.isPending || !selectedCustomer}
              className="gap-2"
            >
              {addCustomerToServiceLine.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Customer to Service Line'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomerToServiceLineDialog; 